import { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Image, Loader2, Move, Check, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

// Focal Point Selector Component
const FocalPointSelector = ({ imageUrl, focalPoint, onFocalPointChange, onConfirm, onCancel, aspectRatio }) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [zoom, setZoom] = useState(1);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    updateFocalPoint(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      updateFocalPoint(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const updateFocalPoint = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    onFocalPointChange({ x, y });
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    updateFocalPointTouch(e.touches[0]);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (isDragging) {
      updateFocalPointTouch(e.touches[0]);
    }
  };

  const updateFocalPointTouch = (touch) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((touch.clientY - rect.top) / rect.height) * 100));
    onFocalPointChange({ x, y });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b bg-slate-50">
          <h3 className="font-semibold text-slate-900 text-lg">Set Image Focus Point</h3>
          <p className="text-sm text-slate-500">Click or drag to set where the image should focus. This ensures faces and important details stay visible.</p>
        </div>
        
        {/* Image with focal point selector */}
        <div className="p-4 bg-slate-100">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Main selector */}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-2 uppercase tracking-wide">Click to set focus point</p>
              <div 
                ref={containerRef}
                className="relative cursor-crosshair rounded-lg overflow-hidden border-2 border-slate-300 shadow-inner"
                style={{ aspectRatio: '16/9' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={() => setIsDragging(false)}
              >
                <img 
                  src={imageUrl} 
                  alt="Set focal point" 
                  className="w-full h-full object-cover pointer-events-none"
                  style={{ 
                    transform: `scale(${zoom})`,
                    transformOrigin: `${focalPoint.x}% ${focalPoint.y}%`
                  }}
                  draggable={false}
                />
                {/* Focal point indicator */}
                <div 
                  className="absolute w-10 h-10 pointer-events-none transform -translate-x-1/2 -translate-y-1/2 transition-all duration-75"
                  style={{ left: `${focalPoint.x}%`, top: `${focalPoint.y}%` }}
                >
                  <div className="absolute inset-0 bg-white/30 rounded-full animate-ping" />
                  <div className="absolute inset-2 bg-orange-500 rounded-full border-2 border-white shadow-lg" />
                  <div className="absolute inset-0 border-2 border-white/50 rounded-full" />
                </div>
                {/* Crosshair guides */}
                <div 
                  className="absolute top-0 bottom-0 w-px bg-white/40 pointer-events-none"
                  style={{ left: `${focalPoint.x}%` }}
                />
                <div 
                  className="absolute left-0 right-0 h-px bg-white/40 pointer-events-none"
                  style={{ top: `${focalPoint.y}%` }}
                />
              </div>
            </div>
            
            {/* Preview */}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-2 uppercase tracking-wide">Preview (how it will appear)</p>
              <div 
                className="relative rounded-lg overflow-hidden border-2 border-emerald-400 shadow-lg"
                style={{ aspectRatio: aspectRatio === 'aspect-square' ? '1/1' : aspectRatio === 'aspect-[21/9]' ? '21/9' : '16/9' }}
              >
                <img 
                  src={imageUrl} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                  style={{ objectPosition: `${focalPoint.x}% ${focalPoint.y}%` }}
                />
                <div className="absolute bottom-2 right-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Preview
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">The focus point will stay centered in the frame</p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t bg-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setZoom(z => Math.max(1, z - 0.25))}
              disabled={zoom <= 1}
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-500 w-16 text-center">{Math.round(zoom * 100)}%</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setZoom(z => Math.min(2, z + 0.25))}
              disabled={zoom >= 2}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onConfirm} className="bg-emerald-500 hover:bg-emerald-400">
              <Check className="w-4 h-4 mr-2" />
              Apply Focus Point
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cloudinary upload component with drag-and-drop and focal point
export default function CloudinaryUploader({ 
  onUpload, 
  currentImage = null,
  currentFocalPoint = null,
  onRemove,
  folder = 'sites',
  aspectRatio = 'aspect-video',
  placeholder = 'Drag & drop an image, or click to select',
  className = ''
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showFocalPointSelector, setShowFocalPointSelector] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [focalPoint, setFocalPoint] = useState(currentFocalPoint || { x: 50, y: 50 });

  const uploadToCloudinary = async (file) => {
    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Get signature from backend
      const sigResponse = await api.get('/cloudinary/signature', {
        params: { folder, resource_type: 'image' }
      });
      const { signature, timestamp, cloud_name, api_key } = sigResponse.data;

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', folder);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error('Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
      });

      xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`);
      xhr.send(formData);

      const result = await uploadPromise;
      
      // After upload, show focal point selector
      setPendingImage({ url: result.secure_url, public_id: result.public_id });
      setFocalPoint({ x: 50, y: 50 }); // Reset focal point for new image
      setShowFocalPointSelector(true);
      
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFocalPointConfirm = () => {
    if (pendingImage) {
      onUpload(pendingImage.url, pendingImage.public_id, focalPoint);
      setPendingImage(null);
    }
    setShowFocalPointSelector(false);
  };

  const handleFocalPointCancel = () => {
    setPendingImage(null);
    setShowFocalPointSelector(false);
  };

  const handleEditFocalPoint = (e) => {
    e.stopPropagation();
    if (currentImage) {
      setPendingImage({ url: currentImage, public_id: null });
      setFocalPoint(currentFocalPoint || { x: 50, y: 50 });
      setShowFocalPointSelector(true);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      uploadToCloudinary(file);
    }
  }, [folder]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
    disabled: isUploading
  });

  const handleRemove = (e) => {
    e.stopPropagation();
    onRemove?.();
  };

  // Get focal point for display
  const displayFocalPoint = currentFocalPoint || { x: 50, y: 50 };

  return (
    <div className={className}>
      <div
        {...getRootProps()}
        className={cn(
          "relative border-2 border-dashed rounded-xl transition-all cursor-pointer overflow-hidden",
          aspectRatio,
          isDragActive 
            ? "border-orange-500 bg-orange-50" 
            : "border-slate-300 hover:border-slate-400 bg-slate-50",
          isUploading && "pointer-events-none",
          currentImage && "border-solid border-slate-200"
        )}
      >
        <input {...getInputProps()} />
        
        {currentImage ? (
          // Show current image with focal point applied
          <div className="absolute inset-0">
            <img 
              src={currentImage} 
              alt="Uploaded" 
              className="w-full h-full object-cover"
              style={{ objectPosition: `${displayFocalPoint.x}% ${displayFocalPoint.y}%` }}
            />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/50 transition-all group">
              <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity gap-2">
                <span className="text-white font-medium">Click to replace</span>
                <Button
                  onClick={handleEditFocalPoint}
                  size="sm"
                  variant="secondary"
                  className="bg-white/90 hover:bg-white text-slate-800"
                >
                  <Move className="w-4 h-4 mr-1" />
                  Adjust Position
                </Button>
              </div>
              {onRemove && (
                <button
                  onClick={handleRemove}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {/* Focal point indicator on image */}
            <div 
              className="absolute w-3 h-3 bg-orange-500 rounded-full border-2 border-white shadow-lg pointer-events-none opacity-50"
              style={{ 
                left: `${displayFocalPoint.x}%`, 
                top: `${displayFocalPoint.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
            />
          </div>
        ) : isUploading ? (
          // Upload progress
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-2" />
            <span className="text-sm text-slate-600">{uploadProgress}%</span>
            <div className="w-32 h-1 bg-slate-200 rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-orange-500 transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          // Empty state
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center mb-3",
              isDragActive ? "bg-orange-100" : "bg-slate-100"
            )}>
              {isDragActive ? (
                <Upload className="w-6 h-6 text-orange-500" />
              ) : (
                <Image className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <p className={cn(
              "text-sm text-center",
              isDragActive ? "text-orange-600 font-medium" : "text-slate-500"
            )}>
              {isDragActive ? "Drop image here" : placeholder}
            </p>
            <p className="text-xs text-slate-400 mt-1">JPG, PNG, GIF up to 10MB</p>
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-sm text-red-500 mt-2">{error}</p>
      )}

      {/* Focal Point Selector Modal */}
      {showFocalPointSelector && pendingImage && (
        <FocalPointSelector
          imageUrl={pendingImage.url}
          focalPoint={focalPoint}
          onFocalPointChange={setFocalPoint}
          onConfirm={handleFocalPointConfirm}
          onCancel={handleFocalPointCancel}
          aspectRatio={aspectRatio}
        />
      )}
    </div>
  );
}

// Multi-image gallery uploader
export function GalleryUploader({ images = [], onChange, folder = 'sites/gallery', maxImages = 20 }) {
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async (files) => {
    if (images.length >= maxImages) return;
    
    setIsUploading(true);
    
    try {
      // Get signature
      const sigResponse = await api.get('/cloudinary/signature', {
        params: { folder, resource_type: 'image' }
      });
      const { signature, timestamp, cloud_name, api_key } = sigResponse.data;

      const newImages = [...images];
      
      for (const file of files.slice(0, maxImages - images.length)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('signature', signature);
        formData.append('timestamp', timestamp);
        formData.append('api_key', api_key);
        formData.append('folder', folder);

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
          { method: 'POST', body: formData }
        );
        
        if (response.ok) {
          const result = await response.json();
          newImages.push({ url: result.secure_url, caption: '', public_id: result.public_id });
        }
      }
      
      onChange(newImages);
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    handleUpload(acceptedFiles);
  }, [images, folder]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    disabled: isUploading || images.length >= maxImages
  });

  const removeImage = (idx) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  const updateCaption = (idx, caption) => {
    const updated = [...images];
    updated[idx] = { ...updated[idx], caption };
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {/* Image grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {images.map((img, idx) => (
            <div key={idx} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                <img src={img.url} alt="" className="w-full h-full object-cover" />
              </div>
              <button
                onClick={() => removeImage(idx)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
              <input
                type="text"
                value={img.caption || ''}
                onChange={(e) => updateCaption(idx, e.target.value)}
                placeholder="Caption"
                className="mt-1 w-full text-xs px-2 py-1 border rounded"
              />
            </div>
          ))}
        </div>
      )}
      
      {/* Dropzone */}
      {images.length < maxImages && (
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer text-center",
            isDragActive 
              ? "border-orange-500 bg-orange-50" 
              : "border-slate-300 hover:border-slate-400 bg-slate-50",
            isUploading && "pointer-events-none opacity-50"
          )}
        >
          <input {...getInputProps()} />
          <Upload className={cn(
            "w-8 h-8 mx-auto mb-2",
            isDragActive ? "text-orange-500" : "text-slate-400"
          )} />
          <p className="text-sm text-slate-600">
            {isUploading 
              ? "Uploading..." 
              : isDragActive 
                ? "Drop images here" 
                : "Drag & drop images, or click to select"}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {images.length} / {maxImages} images
          </p>
        </div>
      )}
    </div>
  );
}
